import React from 'react';
import { Select, Badge, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * Select component that uses aggregation data from filter API response
 * This replaces UniversalElasticsearchSelect for filters that should use aggregations
 *
 * @param {Object} props
 * @param {string} props.filterKey - Key for this filter in aggregations
 * @param {Array} props.options - Array of {value, label, count} from aggregations
 * @param {Array} props.value - Selected values
 * @param {Function} props.onChange - Change handler
 * @param {string} props.label - Label translation key
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.showCount - Show option counts
 * @param {boolean} props.disabled - Disable the select
 * @param {boolean} props.showSelectedTags - Show selected items as tags
 */

/**
 * Selected value tags component
 * @param {Object} props
 * @param {Array} props.value - Selected values
 * @param {Array} props.options - Available options
 * @param {Function} props.onRemove - Remove handler
 * @returns {JSX.Element|null}
 */
const SelectedValueTags = ({ value, options, onRemove }) => {
  if (!Array.isArray(value) || value.length === 0) return null;

  const tags = value.map((selectedValue) => {
    const option = options.find((opt) => opt.value === selectedValue);
    const displayLabel = option ? option.label : selectedValue;

    return (
      <Tag
        key={selectedValue}
        closable
        onClose={() => onRemove(selectedValue)}
        className="selected-tag"
      >
        {displayLabel}
      </Tag>
    );
  });

  return <div className="selected-tags-container">{tags}</div>;
};

const AggregationSelect = ({
  filterKey,
  options = [],
  value = [],
  onChange,
  label,
  placeholder,
  showCount = false,
  disabled = false,
  showSelectedTags = true,
}) => {
  const { t } = useTranslation();

  const handleChange = (selectedValues) => {
    const selectedItems = selectedValues
      .map((val) => options.find((opt) => opt.value === val))
      .filter(Boolean);

    onChange(selectedValues, selectedItems);
  };

  /**
   * Handles tag removal
   * @param {string} removedValue - Value to remove
   */
  const handleRemoveTag = (removedValue) => {
    const newValue = Array.isArray(value)
      ? value.filter((v) => v !== removedValue)
      : [];
    
    const selectedItems = newValue
      .map((val) => options.find((opt) => opt.value === val))
      .filter(Boolean);

    onChange(newValue, selectedItems);
  };

  const renderOption = (option) => (
    <div className="elasticsearch-option">
      <span className="option-label">{option.label}</span>
      {showCount && option.count && (
        <Badge count={option.count} className="option-count" />
      )}
    </div>
  );

  return (
    <div className="elasticsearch-select-container">
      <div className="select-header">
        {label && <label className="select-label">{t(label)}</label>}
      </div>

      <Select
        className="elasticsearch-select"
        mode="multiple"
        value={value}
        onChange={handleChange}
        placeholder={placeholder || t('common|Select options')}
        allowClear
        maxTagCount={1}
        optionFilterProp="label"
        filterOption={(input, option) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
        disabled={disabled}
        optionRender={(option) => renderOption(option)}
      >
        {options.map((option) => (
          <Select.Option
            key={option.value}
            value={option.value}
            label={option.label}
          >
            {renderOption(option)}
          </Select.Option>
        ))}
      </Select>

      {showSelectedTags && (
        <SelectedValueTags
          value={value}
          options={options}
          onRemove={handleRemoveTag}
        />
      )}
    </div>
  );
};

export default AggregationSelect;
